import { useCallback, useEffect, useState } from "react";
import { Plus, RefreshCw, Settings } from "lucide-react";
import {
  ApiError,
  api,
  deleteLink as deleteLinkRequest,
  runLinkCheck,
  type LinkView,
  type LinksResponse,
  type RunAllResponse
} from "../api";
import { LinkRow } from "./LinkRow";
import { LinkDialog } from "./LinkDialog";
import { QrDialog } from "./QrDialog";
import { SettingsDialog } from "./SettingsDialog";
import { SummaryCards } from "./SummaryCards";
import { waitForLinkRunResult, waitForMultipleLinkRunResults } from "../linkPolling";
import { useToast } from "../toastContext";

export function Dashboard() {
  const [links, setLinks] = useState<LinkView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [persistentNotice, setPersistentNotice] = useState<string | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<LinkView | null>(null);
  const [qrLink, setQrLink] = useState<LinkView | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const toast = useToast();

  const loadLinks = useCallback(async () => {
    setError(null);
    const response = await api<LinksResponse>("/api/links");
    setLinks(response.links);
    return response.links;
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      try {
        const response = await api<LinksResponse>("/api/links");
        if (!cancelled) {
          setLinks(response.links);
          setError(null);
        }
      } catch (requestError) {
        if (!cancelled) setError(readRequestError(requestError, "Failed to load links"));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  async function handleRunAll() {
    setIsRunning(true);
    setPersistentNotice(null);
    setError(null);
    const toastId = "run-all-checks";
    try {
      const result = await api<RunAllResponse>("/api/run-all", { method: "POST" });
      await loadLinks();
      const failureCount = result.failures?.length ?? 0;
      const runCount = result.runs.length;
      if (failureCount > 0) {
        setPersistentNotice(`${runCount} queued, ${failureCount} failed to queue`);
      }
      if (runCount === 0) {
        toast.showToast({
          tone: "info",
          title: "No enabled links"
        });
        return;
      }

      toast.showToast({
        id: toastId,
        tone: "loading",
        title: `Checking ${runCount} subscriptions...`
      });
      const pollResult = await waitForMultipleLinkRunResults({
        runs: result.runs,
        loadLinks
      });
      if (pollResult.status === "completed") {
        toast.updateToast(toastId, { tone: "success", title: `${runCount} checks completed` });
      } else if (pollResult.status === "missing") {
        toast.dismissToast(toastId);
        setPersistentNotice(`${runCount} checks queued; ${pollResult.missingRuns.length} links were removed before results refreshed`);
      } else {
        toast.dismissToast(toastId);
        setPersistentNotice(`${runCount} checks queued; ${pollResult.pendingRuns.length} still pending, refresh if results do not appear soon`);
      }
    } catch (requestError) {
      toast.dismissToast(toastId);
      setError(readRequestError(requestError, "Failed to run checks"));
    } finally {
      setIsRunning(false);
    }
  }

  async function handleRunLink(link: LinkView) {
    setIsRunning(true);
    setPersistentNotice(null);
    setError(null);
    const toastId = `run-link-${link.id}`;
    try {
      const { runId } = await runLinkCheck(link.id);
      toast.showToast({ id: toastId, tone: "loading", title: `Checking ${link.name}...` });
      const result = await waitForLinkRunResult({
        linkId: link.id,
        runId,
        loadLinks
      });
      if (result.status === "completed") {
        toast.updateToast(toastId, { tone: "success", title: `Check completed for ${result.link.name}` });
      } else if (result.status === "missing") {
        toast.dismissToast(toastId);
        setPersistentNotice(`Check queued for ${link.name}; link was removed before the result refreshed`);
      } else {
        toast.dismissToast(toastId);
        setPersistentNotice(`Check queued for ${link.name}; refresh if the result does not appear soon`);
      }
    } catch (requestError) {
      toast.dismissToast(toastId);
      setError(readRequestError(requestError, "Failed to run check"));
    } finally {
      setIsRunning(false);
    }
  }

  async function handleDeleteLink(link: LinkView) {
    if (!window.confirm(`Delete ${link.name}?`)) return;

    setPersistentNotice(null);
    setError(null);
    try {
      await deleteLinkRequest(link.id);
      if (editingLink?.id === link.id) setEditingLink(null);
      if (qrLink?.id === link.id) setQrLink(null);
      await loadLinks();
      toast.showToast({ tone: "success", title: `Deleted ${link.name}` });
    } catch (requestError) {
      setError(readRequestError(requestError, "Failed to delete link"));
    }
  }

  async function handleLinkSaved() {
    try {
      await loadLinks();
      toast.showToast({ tone: "success", title: "Link saved" });
    } catch (requestError) {
      setError(readRequestError(requestError, "Failed to reload links"));
    }
  }

  function closeLinkDialog() {
    setIsAddOpen(false);
    setEditingLink(null);
  }

  return (
    <main className="min-h-screen bg-[#120f0c] text-[#f4f0ec]">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-5 sm:px-6 sm:py-7 lg:px-8">
        <header className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <img
              src="/submonitor-logo.png"
              alt=""
              aria-hidden="true"
              className="h-10 w-10 shrink-0 object-contain sm:h-11 sm:w-11"
            />
            <h1 className="truncate text-2xl font-semibold tracking-normal text-white sm:text-3xl">SubMonitor</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="icon-button"
              type="button"
              aria-label="Add link"
              title="Add link"
              onClick={() => setIsAddOpen(true)}
            >
              <Plus aria-hidden="true" className="h-5 w-5" />
            </button>
            <button
              className="icon-button icon-button-accent"
              type="button"
              aria-label="Run all checks"
              title="Run all checks"
              onClick={handleRunAll}
              disabled={isRunning || isLoading}
            >
              <RefreshCw aria-hidden="true" className={`h-5 w-5 ${isRunning ? "animate-spin" : ""}`} />
            </button>
            <button
              className="icon-button"
              type="button"
              aria-label="Settings"
              title="Settings"
              onClick={() => setIsSettingsOpen(true)}
            >
              <Settings aria-hidden="true" className="h-5 w-5" />
            </button>
          </div>
        </header>

        {error ? (
          <div role="alert" className="rounded-md border border-[#60312d] bg-[#2a1111] px-4 py-3 text-sm text-[#ff9a91]">
            {error}
          </div>
        ) : null}

        {persistentNotice ? (
          <div className="rounded-md border border-[#263f5d] bg-[#0b1824] px-4 py-3 text-sm text-[#7ec7ff]">
            {persistentNotice}
          </div>
        ) : null}

        <SummaryCards links={links} isLoading={isLoading} />

        <section className="space-y-3" aria-label="Monitored links">
          {isLoading ? (
            <LoadingRows />
          ) : links.length > 0 ? (
            links.map((link) => (
              <LinkRow
                key={link.id}
                link={link}
                onQr={setQrLink}
                onEdit={setEditingLink}
                onRun={handleRunLink}
                onDelete={handleDeleteLink}
              />
            ))
          ) : (
            <EmptyState onAdd={() => setIsAddOpen(true)} />
          )}
        </section>

        <LinkDialog
          open={isAddOpen || Boolean(editingLink)}
          link={editingLink}
          onClose={closeLinkDialog}
          onSaved={handleLinkSaved}
        />
        <SettingsDialog open={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
        <QrDialog link={qrLink} onClose={() => setQrLink(null)} />
      </div>
    </main>
  );
}

function LoadingRows() {
  return (
    <>
      {[0, 1, 2].map((item) => (
        <div
          key={item}
          className="h-[92px] animate-pulse rounded-lg border border-[#24201b] bg-[#08090b] sm:h-[76px]"
        />
      ))}
    </>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-start justify-between gap-4 rounded-lg border border-[#24201b] bg-[#08090b] px-4 py-5 sm:flex-row sm:items-center">
      <div>
        <h2 className="text-base font-medium text-white">No links yet</h2>
        <p className="mt-1 text-sm text-[#8f877f]">Monitoring list is empty.</p>
      </div>
      <button className="accent-button" type="button" onClick={onAdd}>
        <Plus aria-hidden="true" className="h-4 w-4" />
        Add
      </button>
    </div>
  );
}

function readRequestError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return fallback;
}
